import { LiveValue, type Live, type LocalPrefs, type LocalPrefsService, type SessionLifetime } from '@/model';

export const defaultPrefs: LocalPrefs = { theme: 'system', language: 'system', textSize: 'default', enterToSend: true, showsPins: true, linkPreviews: true, messageWidth: 'standard', wallpaper: 'standard', inTabAlerts: true, themePack: 'calm', density: 'default', messageLayout: 'default' };
const key = 'hylo.web.prefs';
function rawPrefs(value: string | null): Record<string, unknown> {
  try { const parsed: unknown = JSON.parse(value ?? 'null'); return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed as Record<string, unknown> : {}; } catch { return {}; }
}
function effective(raw: Record<string, unknown>, appearance: Record<string, unknown> = {}): LocalPrefs {
  const result = { ...defaultPrefs };
  const choices = {
    theme: ['system', 'light', 'dark'], language: ['system', 'en', 'zh-Hans'],
    textSize: ['system', 'smallest', 'smaller', 'small', 'default', 'large', 'larger', 'xlarge'],
    messageWidth: ['standard', 'wide', 'full'], wallpaper: ['standard', 'dawn', 'lagoon', 'dusk', 'graphite'],
  } as const;
  for (const field of Object.keys(choices) as (keyof typeof choices)[]) if ((choices[field] as readonly unknown[]).includes(raw[field])) Object.assign(result, { [field]: raw[field] });
  for (const field of ['enterToSend', 'showsPins', 'linkPreviews', 'inTabAlerts'] as const) if (typeof raw[field] === 'boolean') result[field] = raw[field];
  if (typeof appearance.themePack === 'string') result.themePack = appearance.themePack;
  if ((['default', 'comfortable', 'compact'] as readonly unknown[]).includes(appearance.density)) result.density = appearance.density as LocalPrefs['density'];
  if ((['default', 'bubbles', 'workspace'] as readonly unknown[]).includes(appearance.messageLayout)) result.messageLayout = appearance.messageLayout as LocalPrefs['messageLayout'];
  return result;
}
export interface PrefsContext { server?: string; account?: Live<SessionLifetime | null> }
interface StoredPrefs { raw: Record<string, unknown>; stored: string | null | undefined }
const empty = (): StoredPrefs => ({ raw: {}, stored: undefined });
const appearanceFields = new Set(['themePack', 'density', 'messageLayout']);
function serverOrigin(server?: string): string {
  try { const origin = new URL(server ?? globalThis.location?.origin ?? 'http://localhost').origin; if (origin !== 'null') return origin; } catch { /* An anonymous/non-browser host uses the local fallback. */ }
  return 'http://localhost';
}
/** Versioned appearance records contain only non-secret server/account identity and local preferences. */
export function appearanceStorageKey(server: string, accountId: string): string { return `hylo.web.appearance.v1:${JSON.stringify([serverOrigin(server), accountId])}`; }
export function createPrefs(storage?: Storage, context: PrefsContext = {}): LocalPrefsService & { dispose(): void } {
  const global = empty(), accounts = new Map<string, StoredPrefs>(), server = serverOrigin(context.server);
  let anonymous = empty(), disposed = false;
  const accountKey = () => { const id = context.account?.get()?.accountId; return id?.trim() ? appearanceStorageKey(server, id) : undefined; };
  let activeKey = accountKey();
  const appearance = () => {
    if (!activeKey) return anonymous;
    let entry = accounts.get(activeKey); if (!entry) { entry = empty(); accounts.set(activeKey, entry); } return entry;
  };
  const read = (entry: StoredPrefs, storageKey: string) => {
    try {
      const latest = storage?.getItem(storageKey);
      // A failed write keeps its in-memory value until storage actually changes.
      if (latest !== undefined && latest !== entry.stored) { entry.stored = latest; entry.raw = rawPrefs(latest); }
    } catch { /* Storage denial must not merge or forget other accounts' in-memory choices. */ }
  };
  const write = (entry: StoredPrefs, storageKey: string) => {
    try { if (storage) { const encoded = JSON.stringify(entry.raw); storage.setItem(storageKey, encoded); entry.stored = encoded; } }
    catch { /* Retain the pending local value, with the last observed storage value for reconciliation. */ }
  };
  const refresh = () => { read(global, key); if (activeKey) read(appearance(), activeKey); };
  refresh();
  const prefs = new LiveValue(effective(global.raw, appearance().raw));
  const publish = () => { const next = effective(global.raw, appearance().raw); if (JSON.stringify(next) !== JSON.stringify(prefs.get())) prefs.set(next); };
  const selectAccount = () => {
    if (disposed) return;
    const next = accountKey(); if (next !== activeKey) { activeKey = next; if (!next) anonymous = empty(); }
    refresh(); publish();
  };
  const stopAccount = context.account?.subscribe(selectAccount);
  const changed = (event: StorageEvent) => {
    if (disposed || event.storageArea !== storage || event.key !== null && event.key !== key && event.key !== activeKey) return;
    if (event.key === null) { global.raw = {}; global.stored = undefined; accounts.clear(); anonymous = empty(); }
    // Read current storage, not the event payload: an older queued event must not roll back a newer write.
    refresh(); publish();
  };
  if (storage && globalThis.window) window.addEventListener('storage', changed);
  return {
    prefs,
    set(patch) {
      if (disposed) return;
      refresh();
      const install: Record<string, unknown> = {}, scoped: Record<string, unknown> = {};
      for (const [field, value] of Object.entries(patch)) (appearanceFields.has(field) ? scoped : install)[field] = value;
      if (Object.keys(install).length) { global.raw = { ...global.raw, ...install }; write(global, key); }
      if (Object.keys(scoped).length) {
        const entry = appearance(); entry.raw = { version: 1, ...entry.raw, ...scoped };
        // Anonymous previews are memory-only and cannot seed the next signed-in account.
        if (activeKey) write(entry, activeKey);
      }
      publish();
    },
    dispose() { disposed = true; stopAccount?.(); if (storage && globalThis.window) window.removeEventListener('storage', changed); },
  };
}
